import type { FilterSelection } from 'bnef';

// A Map-based type for safely accumulating user-supplied key-value pairs.
// Maps are immune to prototype pollution since keys are stored in an internal hash table,
// not as properties on the object prototype chain.
export type QueryMap = Map<string, string | string[] | QueryMap>;

// Recursively converts a Map (including nested Maps and arrays containing Maps) to a plain object
// suitable for JSON.stringify. Use at serialization boundaries (e.g. before sending API request bodies).
export const mapToJson = (map: Map<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    for (const [key, value] of map.entries()) {
        if (value instanceof Map) {
            result[key] = mapToJson(value);
        } else if (Array.isArray(value)) {
            result[key] = value.map((item) => (item instanceof Map ? mapToJson(item) : item));
        } else {
            result[key] = value;
        }
    }
    return result;
};

export const cleanFilters = (...filters: any[]) => filters.filter((f) => Boolean(f?.nameKey || f?.label));

export const allFiltersEmpty = (filters: Record<string, FilterSelection>) =>
    Object.keys(filters).some((activeKey) => {
        let isNotEmpty = Boolean(filters[activeKey]);
        if (filters[activeKey] instanceof Set) {
            isNotEmpty = (filters[activeKey] as Set<string>).size > 0;
        } else if (filters[activeKey] instanceof Object) {
            isNotEmpty = Object.keys(filters[activeKey]).length > 0;
        }
        return isNotEmpty;
    }) === false;

export const extractItemFromPath = (obj: any, path: string): any => {
    const parts = path.split('.');
    let target = obj;
    let currentKey = parts.shift();
    while (currentKey) {
        if (!target || !Object.hasOwn(target, currentKey)) {
            return undefined;
        }
        target = target[currentKey];
        currentKey = parts.shift();
    }
    return target;
};
